import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import {QueryingConnectionSearchInput} from 'instruments/connections/ConnectionSearchInput';
import {InstrumentData} from 'instruments/instrument-data/InstrumentDataConstants';
import InstrumentDataTable from 'instruments/instrument-data/InstrumentDataTable';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {FilterKey} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import {ConnectionListingGraphQL} from '../../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';

export function ConnectionSelector({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const connectionId = (config.connectionId as string) ?? null;

  const handleChangeConnection = useCallback(
    (connections: Array<ConnectionListingGraphQL>) => {
      if (connections.length === 0) {
        return;
      }
      const newConnectionId = connections[0].id;
      if (newConnectionId !== connectionId) {
        // When connection changes, clear the file selection since files are connection-specific
        const updatedConfig = setValueInConfig(config, ['connectionFileId'], null);
        onChangeConfig(updatedConfig, ['connectionId'], newConnectionId);
      }
    },
    [config, connectionId, onChangeConfig]
  );

  return (
    <div className={css(styles.wrapper)}>
      <QueryingConnectionSearchInput
        autoFocus={false}
        onChange={handleChangeConnection}
        connectionIds={connectionId != null ? [connectionId] : []}
        shouldAllowMultiple={false}
        disabled={disabled}
      />
    </div>
  );
}

export function ConnectionFileSelector({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const connectionId = (config.connectionId as string) ?? null;
  const connectionFileId = (config.connectionFileId as string) ?? null;
  const [instrumentData, setInstrumentData] = useState<Array<InstrumentData>>([]);

  const handleSelectRow = useCallback(
    (rowIndex: number) => {
      const selectedFile = instrumentData[rowIndex];
      if (selectedFile != null) {
        onChangeConfig(config, ['connectionFileId'], selectedFile.id);
      }
    },
    [config, instrumentData, onChangeConfig]
  );

  const selectedRowIndices = useMemo(() => {
    const idx = connectionFileId != null ? instrumentData.findIndex(({id}) => id === connectionFileId) : -1;
    return new Set(idx >= 0 ? [idx] : []);
  }, [connectionFileId, instrumentData]);

  if (connectionId == null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Select a connection first to browse available files."
      />
    );
  }

  return (
    <div className={css(styles.wrapper)}>
      <InstrumentDataTable
        instrumentData={instrumentData}
        allowMultiple={false}
        setInstrumentData={setInstrumentData}
        selectedRowIndices={selectedRowIndices}
        onSelectRow={handleSelectRow}
        columns={['File name', 'Status', 'Data type', 'Modified at']}
        filterOptions={[]}
        initialFilterValues={{[FilterKey.CONNECTION]: [connectionId]}}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 8,
  },
});
